// Shared Watermark Configuration
// Unified constants and utility functions for watermark positioning across frontend and backend

import type { CSSProperties } from 'react';

// ==================== CONSTANTS ====================

export const WATERMARK_CONFIG = {
  // Margin calculation (responsive based on image size)
  MARGIN_PERCENT: 2,    // 2% of smaller dimension
  MARGIN_MIN_PX: 12,    // Minimum 12px margin
  MARGIN_MAX_PX: 20,    // Maximum 20px margin (for very large images)

  // Default values
  DEFAULT_AGENCY_OPACITY: 0.8,
  DEFAULT_AGENCY_SCALE: 1.0,
  DEFAULT_WYSKO_OPACITY: 0.3,
  DEFAULT_WYSKO_SCALE: 1.0,

  // Proportional overlap offset when both watermarks in same corner
  OVERLAP_OFFSET_PERCENT: 6,  // 6% of smaller dimension

  // Proportional sizing - base size as % of image's smaller dimension
  // No absolute px clamping — ensures identical proportions across preview and download
  AGENCY_BASE_SIZE_PERCENT: 8,
  WYSKO_BASE_SIZE_PERCENT: 5,
} as const;

// ==================== TYPES ====================

export type WatermarkPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface WatermarkDisplaySettings {
  enabled: boolean;
  position: WatermarkPosition;
  opacity: number;    // Normalized 0-1
  scale: number;      // Normalized multiplier (0.5-2.0)
  logoUrl?: string | null;
}

export interface NormalizedWatermarkSettings {
  agency: WatermarkDisplaySettings;
  wysko: WatermarkDisplaySettings;
}

// Raw settings from different sources
export interface RawWatermarkSettings {
  watermark_enabled?: boolean;
  watermark_position?: string;
  watermark_opacity?: number;
  watermark_scale?: number;
  watermark_logo_url?: string | null;
  wysko_watermark_enabled?: boolean;
  wysko_watermark_position?: string;
  wysko_watermark_opacity?: number;
  wysko_watermark_scale?: number;
}

// ==================== NORMALIZATION ====================

/**
 * Normalize watermark settings from various sources to a consistent format.
 * Handles the inconsistency between how local state and DB store values.
 *
 * @param raw - Raw settings object
 * @param source - 'db' for database format, 'local' for settings component local state
 */
export function normalizeWatermarkSettings(
  raw: RawWatermarkSettings,
  source: 'db' | 'local'
): NormalizedWatermarkSettings {
  let agencyOpacity: number;
  let agencyScale: number;
  let wyskoOpacity: number;
  let wyskoScale: number;

  if (source === 'db') {
    // DB stores:
    // - opacity as 0.0-1.0
    // - agency scale as 0.5-2.0
    // - wysko scale as 50-200 (inconsistent!)
    agencyOpacity = raw.watermark_opacity ?? WATERMARK_CONFIG.DEFAULT_AGENCY_OPACITY;
    agencyScale = raw.watermark_scale ?? WATERMARK_CONFIG.DEFAULT_AGENCY_SCALE;
    wyskoOpacity = raw.wysko_watermark_opacity ?? WATERMARK_CONFIG.DEFAULT_WYSKO_OPACITY;
    // IMPORTANT: wysko_watermark_scale in DB is 50-200, convert to 0.5-2.0
    wyskoScale = (raw.wysko_watermark_scale ?? 100) / 100;
  } else {
    // Local state (settings component) stores:
    // - opacity as 0-100
    // - scale as 50-200
    agencyOpacity = (raw.watermark_opacity ?? 80) / 100;
    agencyScale = (raw.watermark_scale ?? 100) / 100;
    wyskoOpacity = (raw.wysko_watermark_opacity ?? 30) / 100;
    wyskoScale = (raw.wysko_watermark_scale ?? 100) / 100;
  }

  return {
    agency: {
      enabled: raw.watermark_enabled ?? false,
      position: (raw.watermark_position as WatermarkPosition) ?? 'bottom-right',
      opacity: agencyOpacity,
      scale: agencyScale,
      logoUrl: raw.watermark_logo_url,
    },
    wysko: {
      enabled: raw.wysko_watermark_enabled ?? false,
      position: (raw.wysko_watermark_position as WatermarkPosition) ?? 'bottom-right',
      opacity: wyskoOpacity,
      scale: wyskoScale,
    },
  };
}

// ==================== MARGIN CALCULATION ====================

/**
 * Calculate responsive margin based on image dimensions.
 * Uses percentage of smaller dimension, clamped between min and max.
 */
export function calculateWatermarkMargin(width: number, height: number): number {
  const minDimension = Math.min(width, height);
  const marginPercent = minDimension * (WATERMARK_CONFIG.MARGIN_PERCENT / 100);
  return Math.max(
    WATERMARK_CONFIG.MARGIN_MIN_PX,
    Math.min(WATERMARK_CONFIG.MARGIN_MAX_PX, marginPercent)
  );
}

// ==================== PROPORTIONAL SIZE CALCULATION ====================

/**
 * Calculate watermark display size based on image dimensions.
 * Returns max-height and max-width values for constraining the watermark.
 * This ensures consistent proportional sizing across frontend preview and backend download.
 *
 * @param imageWidth - Displayed image width in pixels
 * @param imageHeight - Displayed image height in pixels
 * @param type - 'agency' or 'wysko'
 * @param userScale - User's scale setting (0.5-2.0)
 */
export function calculateWatermarkSize(
  imageWidth: number,
  imageHeight: number,
  type: 'agency' | 'wysko',
  userScale: number = 1.0
): { maxHeight: number; maxWidth: number } {
  const minDimension = Math.min(imageWidth, imageHeight);

  const basePercent = type === 'agency'
    ? WATERMARK_CONFIG.AGENCY_BASE_SIZE_PERCENT
    : WATERMARK_CONFIG.WYSKO_BASE_SIZE_PERCENT;

  // Pure proportional calculation — no absolute px clamping
  // This guarantees identical proportions across settings preview, lightbox, and downloaded image
  const finalSize = Math.max(1, minDimension * (basePercent / 100) * userScale);

  return {
    maxHeight: finalSize,
    maxWidth: finalSize * 2.5  // Assume max aspect ratio of 2.5:1 for logos
  };
}

// ==================== POSITION CALCULATION ====================

/**
 * Calculate watermark position styles for CSS.
 * Uses responsive margins based on actual image dimensions.
 *
 * @param containerWidth - Width of the container (actual displayed image width)
 * @param containerHeight - Height of the container (actual displayed image height)
 * @param position - Corner position
 * @param isSecondary - Whether this is the secondary watermark (for overlap handling)
 * @param otherPosition - Position of the other watermark (for overlap detection)
 */
export function getWatermarkPositionStyles(
  containerWidth: number,
  containerHeight: number,
  position: WatermarkPosition,
  isSecondary: boolean = false,
  otherPosition?: WatermarkPosition
): CSSProperties {
  const margin = calculateWatermarkMargin(containerWidth, containerHeight);

  const base: CSSProperties = { position: 'absolute' };

  // Handle overlap when both watermarks are in same corner
  const sameCorner = isSecondary && otherPosition === position;
  const minDimension = Math.min(containerWidth, containerHeight);
  const overlapOffset = sameCorner
    ? minDimension * (WATERMARK_CONFIG.OVERLAP_OFFSET_PERCENT / 100)
    : 0;

  switch (position) {
    case 'top-left':
      return { ...base, top: margin, left: margin + overlapOffset };
    case 'top-right':
      return { ...base, top: margin, right: margin + overlapOffset };
    case 'bottom-left':
      return { ...base, bottom: margin, left: margin + overlapOffset };
    case 'bottom-right':
    default:
      return { ...base, bottom: margin, right: margin + overlapOffset };
  }
}

// ==================== COMPLETE WATERMARK STYLES ====================

/**
 * Get complete CSS styles for a watermark element.
 * Combines position, opacity, scale, and transform origin.
 */
export function getWatermarkStyles(
  containerWidth: number,
  containerHeight: number,
  settings: WatermarkDisplaySettings,
  isSecondary: boolean = false,
  otherPosition?: WatermarkPosition,
  zIndex: number = 10
): CSSProperties {
  const positionStyles = getWatermarkPositionStyles(
    containerWidth,
    containerHeight,
    settings.position,
    isSecondary,
    otherPosition
  );

  return {
    ...positionStyles,
    opacity: settings.opacity,
    // Note: scale is now baked into size calculation via calculateWatermarkSize()
    // No transform needed - this ensures consistent sizing with backend
    pointerEvents: 'none',
    zIndex,
  };
}
